import React from "react";
import type { ShoppingListItem, SourceId } from "../../types";
import { ALL_SOURCES, SOURCE_LABELS } from "../../types";
import { SourceBadge } from "./SourceBadge";
import { extractQuantity } from "../../unit-price";

interface Props {
  items: ShoppingListItem[];
  onBack: () => void;
}

function formatPrice(price: number): string {
  return `R$ ${price.toFixed(2)}`;
}

interface SourceTotal {
  source: SourceId;
  total: number;
  itemCount: number;
  missingCount: number;
}

function computeTotals(items: ShoppingListItem[]): SourceTotal[] {
  return ALL_SOURCES.map((source) => {
    let total = 0;
    let itemCount = 0;
    let missingCount = 0;
    for (const item of items) {
      const match = item.matches[source];
      if (match) {
        total += match.price;
        itemCount++;
      } else {
        missingCount++;
      }
    }
    return { source, total, itemCount, missingCount };
  }).sort((a, b) => {
    // Sort by completeness first, then by total
    if (a.missingCount !== b.missingCount)
      return a.missingCount - b.missingCount;
    return a.total - b.total;
  });
}

interface PriceDiff {
  itemName: string;
  cheapestSource: SourceId;
  cheapestPrice: number;
  expensiveSource: SourceId;
  expensivePrice: number;
  diff: number;
}

function computeTopDiffs(items: ShoppingListItem[]): PriceDiff[] {
  const diffs: PriceDiff[] = [];

  for (const item of items) {
    const prices: { source: SourceId; price: number }[] = [];
    for (const source of ALL_SOURCES) {
      const match = item.matches[source];
      if (match) prices.push({ source, price: match.price });
    }
    if (prices.length < 2) continue;

    prices.sort((a, b) => a.price - b.price);
    const cheapest = prices[0];
    const expensive = prices[prices.length - 1];
    diffs.push({
      itemName: item.primary.name,
      cheapestSource: cheapest.source,
      cheapestPrice: cheapest.price,
      expensiveSource: expensive.source,
      expensivePrice: expensive.price,
      diff: expensive.price - cheapest.price,
    });
  }

  return diffs.sort((a, b) => b.diff - a.diff).slice(0, 5);
}

function hasMixedSizes(items: ShoppingListItem[]): boolean {
  for (const item of items) {
    const quantities: string[] = [];
    for (const source of ALL_SOURCES) {
      const match = item.matches[source];
      if (!match) continue;
      const qty = extractQuantity(match.name);
      if (qty) quantities.push(`${qty.amount}${qty.unit}`);
    }
    const unique = new Set(quantities);
    if (unique.size > 1) return true;
  }
  return false;
}

export function Comparison({ items, onBack }: Props) {
  const totals = computeTotals(items);
  const topDiffs = computeTopDiffs(items);
  const cheapest = totals[0];
  const secondCheapest = totals.length > 1 ? totals[1] : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Comparacao</h2>
        <button
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Voltar para lista
        </button>
      </div>

      {hasMixedSizes(items) && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg mb-4 flex items-start gap-2">
          <span className="text-yellow-600 dark:text-yellow-400 flex-shrink-0">&#9888;</span>
          <div>
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Tamanhos diferentes entre mercados
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300">
              Alguns itens casaram com embalagens de tamanhos diferentes. Compare pelo preco/kg na lista.
            </p>
          </div>
        </div>
      )}

      {/* Savings highlight */}
      {secondCheapest && cheapest.total < secondCheapest.total && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg mb-6">
          <p className="text-green-800 dark:text-green-200 font-medium">
            Voce economiza{" "}
            {formatPrice(secondCheapest.total - cheapest.total)} comprando no{" "}
            {SOURCE_LABELS[cheapest.source]} vs{" "}
            {SOURCE_LABELS[secondCheapest.source]}
          </p>
        </div>
      )}

      {/* Totals per source */}
      <div className="space-y-2 mb-8">
        {totals.map((t, i) => (
          <div
            key={t.source}
            className={`flex items-center justify-between p-3 rounded-lg border ${
              i === 0
                ? "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800"
                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold text-gray-400 w-6">
                {i + 1}.
              </span>
              <SourceBadge source={t.source} />
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {t.itemCount}/{items.length} itens
                {t.missingCount > 0 && ` (${t.missingCount} sem preco)`}
              </span>
            </div>
            <span
              className={`text-lg font-bold ${
                i === 0 ? "text-green-700 dark:text-green-400" : "text-gray-900 dark:text-gray-100"
              }`}
            >
              {formatPrice(t.total)}
            </span>
          </div>
        ))}
      </div>

      {/* Top price differences */}
      {topDiffs.length > 0 && (
        <div>
          <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Maiores diferencas de preco
          </h3>
          <div className="space-y-2">
            {topDiffs.map((d, i) => (
              <div
                key={i}
                className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1 truncate">
                  {d.itemName}
                </p>
                <div className="flex items-center gap-2 text-xs">
                  <SourceBadge source={d.cheapestSource} />
                  <span className="font-bold text-green-700 dark:text-green-400">
                    {formatPrice(d.cheapestPrice)}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500">vs</span>
                  <SourceBadge source={d.expensiveSource} />
                  <span className="font-bold text-red-600">
                    {formatPrice(d.expensivePrice)}
                  </span>
                  <span className="text-orange-600 font-medium">
                    (+{formatPrice(d.diff)})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
